import * as React from 'react';

import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {SpinnerIcon} from 'shells/icon';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';

import type {GetWhatsAppIntegration_whatsAppIntegration as WhatsAppIntegration} from 'nutshell-graphql-types';

import {useDismissibleUserSetting} from '../../../../../util/hooks';

import {
    getInProgressMessage,
    getCompletionMessage,
    getFailureMessage,
    getPrimaryAction,
    getHelpLink,
} from './helpers';

// my twin lives in Nut\Settings\User.php
const HAS_DISMISSED_WHATSAPP_SYNC_STATUS_BANNER_SETTING_NAME =
    'cuehide_has_dismissed_whatsapp_sync_status_banner';

type Props = {
    whatsAppIntegration: WhatsAppIntegration;
};

export function SyncStatusBanner(props: Props) {
    const {shouldShow, onDismiss} = useDismissibleUserSetting(
        HAS_DISMISSED_WHATSAPP_SYNC_STATUS_BANNER_SETTING_NAME
    );

    const {contactSync, messageSync} = props.whatsAppIntegration;

    if (!shouldShow) {
        return null;
    }

    const handleHidden = () => {
        if (onDismiss) {
            onDismiss();
        }
    };

    const isContactSyncInProgress = contactSync.status === 'IN_PROGRESS';
    const isMessageSyncInProgress = messageSync.status === 'IN_PROGRESS';
    const isContactSyncCompleted = contactSync.status === 'COMPLETED';
    const isMessageSyncCompleted = messageSync.status === 'COMPLETED';
    const isContactSyncFailed = contactSync.status === 'FAILED';
    const isMessageSyncFailed = messageSync.status === 'FAILED';

    const isSyncInProgress = isContactSyncInProgress || isMessageSyncInProgress;
    const isSyncCompleted = isContactSyncCompleted || isMessageSyncCompleted;
    const isSyncFailed = isContactSyncFailed || isMessageSyncFailed;

    if (isSyncInProgress) {
        const message = getInProgressMessage(isContactSyncInProgress, isMessageSyncInProgress);

        return (
            <FlashBanner hasBorder={true} type='info'>
                <StackLegacy isHorizontal={true} alignCenter={true} spacing={8}>
                    <SpinnerIcon size={16} />
                    <Body>{message}</Body>
                </StackLegacy>
            </FlashBanner>
        );
    }

    if (isSyncCompleted) {
        const message = getCompletionMessage(isContactSyncCompleted, isMessageSyncCompleted);
        const primaryAction = getPrimaryAction(
            isContactSyncCompleted,
            isMessageSyncCompleted,
            props.whatsAppIntegration.origin.id
        );

        return (
            <FlashBanner hasBorder={true} type='success' onHidden={handleHidden}>
                <StackLegacy isHorizontal={true} alignCenter={true} spacing={8}>
                    <Body>{message}</Body>
                    {primaryAction && <> {primaryAction}</>}
                </StackLegacy>
            </FlashBanner>
        );
    }

    if (isSyncFailed) {
        const message = getFailureMessage(
            isContactSyncFailed,
            isMessageSyncFailed,
            contactSync.error,
            messageSync.error
        );
        const helpLink = getHelpLink();

        return (
            <FlashBanner hasBorder={true} type='error' onHidden={handleHidden}>
                <StackLegacy isHorizontal={true} alignCenter={true} spacing={16}>
                    <Body>{message}</Body>
                    {helpLink && <> {helpLink}</>}
                </StackLegacy>
            </FlashBanner>
        );
    }

    return null;
}
